import { useTheme } from '@mui/material/styles';
import React from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { determineProjectContext } from '../../assessment/helper';
import { assessmentPolicyHasLabel } from '../../assessment/utils/assessment-utils';
import { getDesignTokens } from '../../base/core';
import { useSnackbarContext } from '../../base/snackbar';
import { useOrganizationProjectContext } from '../../organization-context';

import { usePolicyListAll } from '~/client/hooks/policy';
import * as GQL from '~/graphql/generated/client';
import { GraphDatum } from '~c/components/base/graph-card';

interface DashboardDataContextProps {
  loading: boolean;
  /** True while Phase 2 (full ProjectFragment) is still in flight. Phase 1 data
   *  is already rendered. Use this to show inline loaders on cards that depend
   *  on Phase-2-only fields (policyList, parentAISystem, full
   *  assessments.policy / answerBundles, riskHistory, etc.). */
  phase2Loading: boolean;
  /** True once `projectDimensionsFromId` has returned. Components that render
   *  the dimensions strip should swap in a local skeleton until this flips. */
  dimensionsReady: boolean;
  /** True once `projectEnabledStagesFromId` has returned. The lifecycle
   *  stepper relies on this to avoid rendering empty steps before stages
   *  arrive. */
  stagesReady: boolean;
  opRiskStatus: string;
  opRiskColor: string;
  project: GQL.ProjectFragment | undefined;
  projectDimension: GQL.ProjectDimension[] | undefined | null;
  projectStages: GQL.Stage[] | undefined | null;
  assessments: GQL.AssessmentFragment[];
  /**
   * Whether the current project is an Organization type assessment
   * Use this instead of checking projectType === Organization in components
   */
  isOrganization: boolean;

  error: any;
  technologyRiskStatusData: {
    riskStatus: GQL.AssessmentRiskStatusType;
    color: string;
  };
  alignmentRiskStatusData: {
    riskStatus: GQL.AssessmentRiskStatusType;
    color: string;
  };
  numPolicies: number;
  completionPercent: number;
  projectName: string;
  totalNumControls: number;
  totalNumTestScore: number;
  numTechnologyRiskControls: number;
  technologyRiskData: GraphDatum[];
  policyAlignmentData: GraphDatum[];
  /** Control-level breakdown for the Policy Compliance pie. Sourced directly
   *  from `project.alignmentRiskData.num*Controls` — server-computed so the
   *  dashboard never walks `answerBundles[]` client-side. */
  policyControlPieData: GraphDatum[];
  /** Total visible policy controls — donut center for the Policy Compliance pie. */
  numTotalPolicyControls: number;
  /** Accumulated drill-down rows for the most recently requested
   *  `(riskType, status)` pair. Pages append into this list as the user
   *  clicks "Load more" in the drawer; resets on a fresh `fetchSlice`. Empty
   *  array until the first pie-slice click. */
  sliceRows: GQL.ProjectDrillDownSliceQuery['projectDrillDownSlice']['rows'];
  /** Total rows in the underlying server bucket (independent of how many
   *  pages have been pulled). Used by the drawer to render
   *  "Load more (N remaining)" copy. */
  sliceTotalCount: number;
  /** True when the server has more rows past the currently-loaded set. */
  sliceHasMore: boolean;
  /** Server-side cache version (`ProjectDrillDownCache.updatedAt` ISO string).
   *  Used internally for the version-aware merge — exposed for consumers
   *  that want to react to mid-pagination cache rewrites. */
  sliceCacheVersion: string;
  /** True while a slice fetch (first page or load-more) is in flight. */
  sliceLoading: boolean;
  /** Variables of the most recent slice query response — used by consumers to
   *  detect a stale-response/staleness mismatch (e.g. when a click on slice B
   *  arrives before slice A's response, the previous slice A response stays
   *  visible until B's resolves). Compare against the currently-selected
   *  `(riskType, status)` before rendering rows. Undefined until the first
   *  pie-slice click. */
  sliceVariables:
    | {
        projectId: string;
        riskType: GQL.DrillDownRiskType;
        status: GQL.DrillDownComplianceStatus;
      }
    | undefined;
  /** Fire the slice-scoped lazy query for page 1. Resets accumulated rows.
   *  Apollo caches per `(projectId, riskType, status, offset, limit)`, so
   *  repeat clicks of the same slice hit the cache instantly. */
  fetchSlice: (
    riskType: GQL.DrillDownRiskType,
    status: GQL.DrillDownComplianceStatus
  ) => void;
  /** Fetch the next page for the currently-loaded slice and append the rows.
   *  Silently resets to page 1 if the server's `cacheVersion` changed
   *  between fetches (cache was rewritten by a recompute). No-ops when no
   *  slice is currently loaded or when `sliceHasMore` is false. */
  loadMoreSlice: () => void;
  selectedPolicyLabel: SelectedLabelT[];
  selectedControls: SelectedProps[];
  projectInfo: ProjectInfoT[];
  recommendedPolicy: GQL.PolicyFragment[];
  contextType: ProjectContextType;
  refetchProjectFromId?: (variables?: any) => Promise<any>;
  refetchProjectDimensionsFromId?: (variables?: any) => Promise<any>;
  refetch?: (variables?: any) => Promise<any>;
  getAttachmentsByControlId: (controlId: string) => GQL.AttachmentRef[];
  refetchAttachments: () => Promise<any>;
}
const DashboardDataContext = React.createContext<DashboardDataContextProps>({
  loading: true,
  phase2Loading: true,
  dimensionsReady: false,
  stagesReady: false,
  opRiskStatus: GQL.AssessmentRiskStatusType.Unavailable,
  opRiskColor: '',
  project: undefined,
  projectDimension: undefined,
  projectStages: undefined,
  assessments: [],
  isOrganization: false,
  error: null,
  technologyRiskStatusData: {
    riskStatus: GQL.AssessmentRiskStatusType.Unavailable,
    color: 'initial_color',
  },
  alignmentRiskStatusData: {
    riskStatus: GQL.AssessmentRiskStatusType.Unavailable,
    color: 'initial_color',
  },
  numPolicies: 0,
  completionPercent: 0,
  totalNumControls: 0,
  totalNumTestScore: 0,
  numTechnologyRiskControls: 0,
  projectName: '',
  technologyRiskData: [],
  policyAlignmentData: [],
  policyControlPieData: [],
  numTotalPolicyControls: 0,
  sliceRows: [],
  sliceTotalCount: 0,
  sliceHasMore: false,
  sliceCacheVersion: '',
  sliceLoading: false,
  sliceVariables: undefined,
  fetchSlice: () => undefined,
  loadMoreSlice: () => undefined,
  selectedPolicyLabel: [],
  selectedControls: [],
  projectInfo: [],
  recommendedPolicy: [],
  contextType: 'Project',
  getAttachmentsByControlId: () => [],
  refetchAttachments: () => Promise.resolve(),
});
interface SelectedLabelT {
  name: string;
  selected: boolean;
}
export interface SelectedProps {
  identifier: string | undefined;
  name: string | undefined;
  value?: (string | null | undefined)[];
}
export interface ProjectInfoT {
  identifier: string;
  answer: string | null | undefined;
}
export const useDashboardDataContext = () =>
  React.useContext(DashboardDataContext);

export type ProjectContextType =
  | 'Use Case'
  | 'Create Project'
  | 'Functional Model'
  | 'Model'
  | 'Dataset'
  | 'Project'
  | 'Agent';

export const DashboardDataProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const theme = useTheme();
  const t = React.useMemo(
    () => getDesignTokens(theme.palette.mode, theme.accentPalette),
    [theme.palette.mode, theme.accentPalette]
  );
  const Snackbar = useSnackbarContext();
  const {
    projectId,
    functionalModelId,
    candidateId,
    vendorModelId,
    vendorAgentId,
    assessmentId,
    agentId,
  } = useParams();
  const { pathname } = useLocation();

  // Get cached organization project data from context
  const {
    organizationProjectId,
    organizationProject,
    projectLoading: orgProjectLoading,
    refetchProject: refetchOrgProject,
  } = useOrganizationProjectContext();

  const effectiveId =
    candidateId ??
    agentId ??
    functionalModelId ??
    vendorModelId ??
    vendorAgentId ??
    projectId;

  // Memoized so quick project switches don't race.
  const isViewingOrganizationProject = React.useMemo(() => {
    if (!effectiveId || !organizationProjectId) {
      return false;
    }
    return effectiveId === organizationProjectId;
  }, [effectiveId, organizationProjectId]);

  // Fast first paint; the full query below runs after this one returns.
  const {
    data: dashboardData,
    previousData: previousDashboardData,
    loading: dashboardLoading,
    error,
    refetch: refetchDashboard,
  } = GQL.useProjectForDashboardQuery({
    variables: {
      id: effectiveId ?? '',
    },
    skip: !effectiveId || isViewingOrganizationProject,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  // Only count cached data as ready if it belongs to this project.
  const phase1Ready = Boolean(
    dashboardData?.projectFromId?._id === effectiveId ||
      previousDashboardData?.projectFromId?._id === effectiveId
  );

  // Wait for the fast query to render so they don't run at the same time.
  const {
    data: fullProjectData,
    previousData: previousFullProjectData,
    refetch: refetchFullProject,
  } = GQL.useProjectFromIdQuery({
    variables: {
      id: effectiveId ?? '',
    },
    skip: !effectiveId || isViewingOrganizationProject || !phase1Ready,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  // Use the full query when ready (it has policyList).
  const projectData = React.useMemo(() => {
    if (isViewingOrganizationProject) {
      return organizationProject
        ? { projectFromId: organizationProject }
        : undefined;
    }
    const fullData = fullProjectData ?? previousFullProjectData;
    const dashData = dashboardData ?? previousDashboardData;
    return fullData ?? dashData;
  }, [
    isViewingOrganizationProject,
    organizationProject,
    fullProjectData,
    previousFullProjectData,
    dashboardData,
    previousDashboardData,
  ]);

  const isDataForCurrentProject =
    projectData?.projectFromId?._id === effectiveId;
  const isPhase2DataForCurrentProject =
    fullProjectData?.projectFromId?._id === effectiveId;

  // If Phase 1 errored with no data, flip loading off so the error/retry UI can surface.
  const hasFatalLoadError = Boolean(error && !isDataForCurrentProject);

  const refetchProjectFromId = isViewingOrganizationProject
    ? refetchOrgProject
    : async (variables?: any) => {
        return await refetchDashboard(variables);
      };

  const { data: attachmentsData, refetch: refetchAttachments } =
    GQL.useGetAssessmentAttachmentsQuery({
      variables: {
        projectId: effectiveId ?? '',
        assessmentId: assessmentId ?? '',
      },
      skip: !effectiveId || !assessmentId,
      fetchPolicy: 'cache-first',
    });

  // Defer dimensions and stages queries until Phase 1 has rendered to reduce
  // server contention on initial page load.
  const { data: policyDimensionData, refetch: refetchProjectDimensionsFromId } =
    GQL.useProjectDimensionsFromIdQuery({
      variables: { id: effectiveId ?? '' },
      skip: !effectiveId || !phase1Ready,
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    });
  const { data: projectEnabledStages, refetch } =
    GQL.useProjectEnabledStagesFromIdQuery({
      variables: { id: effectiveId ?? '' },
      skip: !effectiveId || !phase1Ready,
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    });

  const SLICE_PAGE_SIZE = 10;

  // Tells onCompleted whether to replace or append the next response.
  type SliceMergeMode = 'replace' | 'append';
  const pendingFetchModeRef = React.useRef<SliceMergeMode>('replace');
  const [sliceRows, setSliceRows] = React.useState<
    GQL.ProjectDrillDownSliceQuery['projectDrillDownSlice']['rows']
  >([]);
  const [sliceTotalCount, setSliceTotalCount] = React.useState(0);
  const [sliceHasMore, setSliceHasMore] = React.useState(false);
  const [sliceCacheVersion, setSliceCacheVersion] = React.useState('');
  // Refs so callbacks don't get rebuilt every page.
  const sliceRowsLengthRef = React.useRef(0);
  React.useEffect(() => {
    sliceRowsLengthRef.current = sliceRows.length;
  }, [sliceRows.length]);
  const sliceCacheVersionRef = React.useRef('');
  React.useEffect(() => {
    sliceCacheVersionRef.current = sliceCacheVersion;
  }, [sliceCacheVersion]);

  const [triggerSlice, { loading: sliceLoading, variables: sliceVariables }] =
    GQL.useProjectDrillDownSliceLazyQuery({
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
      onCompleted: (data) => {
        const response = data?.projectDrillDownSlice;
        if (!response) {
          return;
        }
        const mode = pendingFetchModeRef.current;
        // Default back to replace so a stray cache hit can't append.
        pendingFetchModeRef.current = 'replace';

        // If the cache changed between pages, start over instead of mixing
        // results from different versions.
        if (mode === 'replace') {
          setSliceRows(response.rows);
        } else if (sliceCacheVersionRef.current === response.cacheVersion) {
          setSliceRows((prev) => [...prev, ...response.rows]);
        } else {
          setSliceRows(response.rows);
        }
        setSliceTotalCount(response.totalCount);
        setSliceHasMore(response.hasMore);
        setSliceCacheVersion(response.cacheVersion);
      },
    });

  const fetchSlice = React.useCallback(
    (
      riskType: GQL.DrillDownRiskType,
      status: GQL.DrillDownComplianceStatus
    ) => {
      if (!effectiveId) {
        return;
      }
      // Clear old rows so we don't show the previous slice's data.
      pendingFetchModeRef.current = 'replace';
      setSliceRows([]);
      setSliceTotalCount(0);
      setSliceHasMore(false);
      setSliceCacheVersion('');
      triggerSlice({
        variables: {
          projectId: effectiveId,
          riskType,
          status,
          offset: 0,
          limit: SLICE_PAGE_SIZE,
        },
      });
    },
    [effectiveId, triggerSlice]
  );

  const loadMoreSlice = React.useCallback(() => {
    if (!effectiveId || !sliceVariables || !sliceHasMore || sliceLoading) {
      return;
    }
    pendingFetchModeRef.current = 'append';
    triggerSlice({
      variables: {
        projectId: sliceVariables.projectId,
        riskType: sliceVariables.riskType,
        status: sliceVariables.status,
        offset: sliceRowsLengthRef.current,
        limit: SLICE_PAGE_SIZE,
      },
    });
  }, [effectiveId, sliceVariables, sliceHasMore, sliceLoading, triggerSlice]);

  // Don't block first paint on these — they have their own readiness flags.
  const dimensionsReady = policyDimensionData !== undefined;
  const stagesReady = projectEnabledStages !== undefined;

  const loading = isViewingOrganizationProject
    ? orgProjectLoading
    : hasFatalLoadError
      ? false
      : !isDataForCurrentProject;

  const phase2Loading =
    !isViewingOrganizationProject &&
    !hasFatalLoadError &&
    !isPhase2DataForCurrentProject;

  // Synchronous so it's ready the same frame loading flips to false.
  const project = React.useMemo<GQL.ProjectFragment | undefined>(() => {
    if (!projectData?.projectFromId) {
      return undefined;
    }
    const sourceData = projectData.projectFromId;
    if (sourceData._id !== effectiveId) {
      return undefined;
    }

    const filteredAssessments = (
      sourceData.assessments as GQL.AssessmentFragment[]
    ).filter((a) => a.status !== GQL.AssessmentStatusType.Archived);

    return {
      ...sourceData,
      assessments: filteredAssessments,
      policyList:
        'policyList' in sourceData
          ? (sourceData as GQL.ProjectFragment).policyList
          : [],
    } as GQL.ProjectFragment;
  }, [projectData, effectiveId]);

  // Avoids re-fetching stages every time assessments update.
  const stagesLoadedForProjectRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    stagesLoadedForProjectRef.current = null;
  }, [effectiveId]);

  // Derive dimensions and stages directly from query data (no useState delay).
  const projectDimension = React.useMemo<
    GQL.ProjectDimension[] | undefined | null
  >(
    () =>
      policyDimensionData
        ? (policyDimensionData.projectDimensionsFromId as GQL.ProjectDimension[])
        : [],
    [policyDimensionData]
  );

  const projectStages = React.useMemo<GQL.Stage[] | undefined | null>(
    () =>
      projectEnabledStages
        ? (projectEnabledStages.projectEnabledStagesFromId as GQL.Stage[])
        : [],
    [projectEnabledStages]
  );

  // The thin Assessment shape works for every page-load consumer.
  // The id check stops stale data from flashing during navigation.
  const assessments = React.useMemo(() => {
    if (!projectData?.projectFromId?.assessments) {
      return [];
    }
    if (projectData.projectFromId._id !== effectiveId) {
      return [];
    }
    return (
      projectData.projectFromId.assessments as GQL.AssessmentFragment[]
    ).filter(
      (assessment) => assessment.status !== GQL.AssessmentStatusType.Archived
    );
  }, [projectData?.projectFromId?.assessments, effectiveId]);

  const { policyList } = usePolicyListAll(undefined, true);

  const projectName = project?.name ?? '';

  // ── All derived dashboard data: synchronous useMemo (no useState/useEffect delay) ──

  // Alignment / policy alignment data.
  //
  // Prefer the server-side `project.alignmentRiskData` (Phase 2) so the
  // top-line "Policy Compliance Risk" label is produced by the manager-
  // approved Layer-4 cascade in computeProjectCompliance. The server
  // already filters out tech-risk assessments + screening (setup-wizard)
  // assessments and applies org thresholds.
  //
  // Fall back to a client-side aggregate over per-assessment riskStatus
  // values while Phase 2 is loading or for surfaces that don't expose the
  // field. The fallback aggregator is intentionally a "worst-wins" rule
  // so the loading state stays conservative; the authoritative verdict
  // arrives with Phase 2. Match the server filter (exclude tech-risk and
  // screening) so the loading-state count and the final count agree.
  const { policyAlignmentData, alignmentRiskStatusData, numPolicies } =
    React.useMemo(() => {
      const policyAssessments = assessments.filter(
        (a) =>
          !a.isTechnologyRiskAssessment &&
          !assessmentPolicyHasLabel(a, 'screening') &&
          !assessmentPolicyHasLabel(a, 'monitoring')
      );

      const serverData = project?.alignmentRiskData;
      const aligned =
        serverData?.aligned ??
        policyAssessments.filter(
          (a) => a.riskStatus === GQL.AssessmentRiskStatusType.Low
        ).length;

      const partiallyCompliant =
        serverData?.partiallyCompliant ??
        policyAssessments.filter(
          (a) => a.riskStatus === GQL.AssessmentRiskStatusType.Medium
        ).length;

      const notAligned =
        serverData?.notAligned ??
        policyAssessments.filter(
          (a) => a.riskStatus === GQL.AssessmentRiskStatusType.High
        ).length;

      const unavailable =
        serverData?.notAvailable ??
        policyAssessments.filter(
          (a) => a.riskStatus === GQL.AssessmentRiskStatusType.Unavailable
        ).length;

      let status: GQL.AssessmentRiskStatusType;
      if (serverData?.status) {
        status = serverData.status;
      } else if (unavailable === policyAssessments.length) {
        status = GQL.AssessmentRiskStatusType.Unavailable;
      } else if (notAligned > 0 || unavailable > 0) {
        status = GQL.AssessmentRiskStatusType.High;
      } else if (partiallyCompliant > 0) {
        status = GQL.AssessmentRiskStatusType.Medium;
      } else if (aligned > 0) {
        status = GQL.AssessmentRiskStatusType.Low;
      } else {
        status = GQL.AssessmentRiskStatusType.Unavailable;
      }

      let color: string;
      switch (status) {
        case GQL.AssessmentRiskStatusType.High:
          color = theme.palette.error.main;
          break;
        case GQL.AssessmentRiskStatusType.Medium:
          color = theme.palette.warning.main;
          break;
        case GQL.AssessmentRiskStatusType.Low:
          color = theme.palette.info.main;
          break;
        default:
          color = t.status.neutral;
      }

      return {
        numPolicies: policyAssessments.length,
        policyAlignmentData: [
          {
            id: 'Fully Compliant',
            label: 'Fully Compliant',
            value: aligned,
            color: t.status.success,
          },
          {
            id: 'Partially Compliant',
            label: 'Partially Compliant',
            value: partiallyCompliant,
            color: t.status.warning,
          },
          {
            id: 'Non Compliant',
            label: 'Non Compliant',
            value: notAligned,
            color: t.status.danger,
          },
          {
            id: 'Unavailable',
            label: 'Unavailable',
            value: unavailable,
            color: t.status.neutral,
          },
        ] as GraphDatum[],
        alignmentRiskStatusData: { riskStatus: status, color },
      };
    }, [assessments, project?.alignmentRiskData, theme, t]);

  // Policy Compliance pie — per-control breakdown, straight from server-
  // computed cachedMetrics. Label is prefixed with the count so the legend
  // reads "420 Fully Compliant" (matches the previous client-side layout).
  const { policyControlPieData, numTotalPolicyControls } = React.useMemo(() => {
    const align = project?.alignmentRiskData;
    const fc = align?.numCompliantControls ?? 0;
    const pc = align?.numPartiallyCompliantControls ?? 0;
    const nc = align?.numNonCompliantControls ?? 0;
    const ua = align?.numUnavailableControls ?? 0;
    const total = align?.numTotalControls ?? fc + pc + nc + ua;
    return {
      policyControlPieData: [
        {
          id: 'Fully Compliant',
          label: `${fc} Fully Compliant`,
          value: fc,
          color: t.status.success,
        },
        {
          id: 'Partially Compliant',
          label: `${pc} Partially Compliant`,
          value: pc,
          color: t.status.warning,
        },
        {
          id: 'Non Compliant',
          label: `${nc} Non Compliant`,
          value: nc,
          color: t.status.danger,
        },
        {
          id: 'Unavailable',
          label: `${ua} Unavailable`,
          value: ua,
          color: t.status.neutral,
        },
      ] as GraphDatum[],
      numTotalPolicyControls: total,
    };
  }, [project?.alignmentRiskData, t]);

  // Inherent risk status + color (formerly operational risk from riskDimensions)
  const { opRiskStatus, opRiskColor } = React.useMemo(() => {
    const status =
      project?.inherentRiskStatus ?? GQL.AssessmentRiskStatusType.Unavailable;

    let color: string;
    switch (status) {
      case GQL.AssessmentRiskStatusType.High:
        color = theme.palette.error.main;
        break;
      case GQL.AssessmentRiskStatusType.Medium:
        color = theme.palette.warning.main;
        break;
      case GQL.AssessmentRiskStatusType.Low:
        color = theme.palette.info.main;
        break;
      default:
        color = theme.palette.info.main;
    }
    return { opRiskStatus: status as string, opRiskColor: color };
  }, [project?.inherentRiskStatus, theme]);

  // Technology risk data + status
  const {
    technologyRiskData,
    technologyRiskStatusData,
    numTechnologyRiskControls,
  } = React.useMemo(() => {
    const techData = project?.technologyRiskData;
    const numTechControls = project?.numTechnologyRiskControls ?? 0;
    if (!techData) {
      return {
        // Mirror the loaded-state shape (4 zero-value entries) so the pie card
        // renders its legend and keeps a constant height before Phase 2 arrives.
        technologyRiskData: [
          {
            id: 'Fully Compliant',
            label: 'Fully Compliant',
            value: 0,
            color: t.status.success,
          },
          {
            id: 'Partially Compliant',
            label: 'Partially Compliant',
            value: 0,
            color: t.status.warning,
          },
          {
            id: 'Non Compliant',
            label: 'Non Compliant',
            value: 0,
            color: t.status.danger,
          },
          {
            id: 'Unavailable',
            label: 'Unavailable',
            value: 0,
            color: t.status.neutral,
          },
        ] as GraphDatum[],
        technologyRiskStatusData: {
          riskStatus: GQL.AssessmentRiskStatusType.Unavailable,
          color: theme.palette.info.main,
        },
        numTechnologyRiskControls: numTechControls,
      };
    }

    let techRiskColor = theme.palette.info.main;
    switch (techData.status) {
      case GQL.AssessmentRiskStatusType.Unverified:
        techRiskColor = t.status.neutral;
        break;
      case GQL.AssessmentRiskStatusType.Unavailable:
        techRiskColor = t.status.neutral;
        break;
      case GQL.AssessmentRiskStatusType.High:
        techRiskColor = theme.palette.error.main;
        break;
      case GQL.AssessmentRiskStatusType.Medium:
        techRiskColor = theme.palette.warning.main;
        break;
      case GQL.AssessmentRiskStatusType.Low:
        techRiskColor = theme.palette.info.main;
        break;
    }

    return {
      technologyRiskData: [
        {
          id: 'Fully Compliant',
          label: 'Fully Compliant',
          value: techData.numCompliant ?? 0,
          color: t.status.success,
        },
        {
          id: 'Partially Compliant',
          label: 'Partially Compliant',
          value: techData.numMediumRisk ?? 0,
          color: t.status.warning,
        },
        {
          id: 'Non Compliant',
          label: 'Non Compliant',
          value: techData.numNonCompliant ?? 0,
          color: t.status.danger,
        },
        {
          id: 'Unavailable',
          label: 'Unavailable',
          value: techData.numNotEvaluated ?? 0,
          color: t.status.neutral,
        },
      ] as GraphDatum[],
      technologyRiskStatusData: {
        riskStatus: techData.status,
        color: techRiskColor,
      },
      numTechnologyRiskControls: numTechControls,
    };
  }, [
    project?.technologyRiskData,
    project?.numTechnologyRiskControls,
    theme,
    t,
  ]);

  // Project metadata: selectedPolicyLabel, selectedControls, projectInfo
  const { selectedPolicyLabel, selectedControls, projectInfo } =
    React.useMemo(() => {
      const metadata = project?.projectMetadata;
      return {
        selectedPolicyLabel: (metadata?.selectedPolicyLabels ?? []).map(
          (name) => ({ name, selected: true })
        ) as SelectedLabelT[],
        selectedControls: (metadata?.selectedControls ?? []).map((c) => ({
          identifier: c.identifier ?? undefined,
          name: c.name ?? undefined,
          value: (c.values ?? []).filter(Boolean) as string[],
        })) as SelectedProps[],
        projectInfo: (metadata?.projectInfo ?? []).map((entry) => ({
          identifier: entry.identifier,
          answer: entry.answer ?? undefined,
        })) as ProjectInfoT[],
      };
    }, [project?.projectMetadata]);

  // Recommended policies (derived from selectedPolicyLabel + policyList)
  const recommendedPolicy = React.useMemo<GQL.PolicyFragment[]>(() => {
    if (!selectedPolicyLabel.length) {
      return [];
    }
    const itemHasLabel = (item: any, labelName: string) => {
      const target = labelName.toLowerCase();
      return (item?.labels ?? []).some(
        (label: string) => label.toLowerCase() === target
      );
    };
    const matchesSelectedLabel = (item: any) =>
      selectedPolicyLabel.some(
        (filter) => filter.selected && itemHasLabel(item, filter.name)
      );
    return policyList.filter(matchesSelectedLabel) ?? [];
  }, [selectedPolicyLabel, policyList]);

  const totalNumControls = project?.numControls ?? 0;
  const totalNumTestScore = project?.numTestScores ?? 0;

  // statistics
  const completionPercent = project?.completionPercent ?? 0;

  const contextType = React.useMemo(
    () =>
      determineProjectContext(
        pathname,
        projectId,
        functionalModelId,
        candidateId
      ),
    [pathname, projectId, functionalModelId, candidateId]
  );

  React.useEffect(() => {
    if (error) {
      Snackbar.open(error);
    }
  }, [error]);

  // Side effect: prefetch stages on initial project load
  React.useEffect(() => {
    if (!project || !projectData) {
      return;
    }

    // Only refetch stages on initial load for this project, not on assessment updates
    const currentProjectId = projectData.projectFromId._id;
    if (
      !isViewingOrganizationProject &&
      currentProjectId &&
      stagesLoadedForProjectRef.current !== currentProjectId
    ) {
      stagesLoadedForProjectRef.current = currentProjectId;
      refetch({ id: currentProjectId });
    }
  }, [project, isViewingOrganizationProject]);

  const getAttachmentsByControlId = React.useCallback(
    (controlId: string) => {
      return (
        attachmentsData?.getAssessmentAttachments?.attachmentsByControl?.[
          controlId
        ] || []
      );
    },
    [attachmentsData]
  );

  // Derive isOrganization from project type
  const isOrganization = project?.projectType === GQL.ProjectType.Organization;

  const contextValue = React.useMemo(() => {
    return {
      project,
      opRiskStatus,
      opRiskColor,
      assessments,
      isOrganization,
      loading,
      phase2Loading,
      dimensionsReady,
      stagesReady,
      projectName,
      projectDimension,
      error,
      technologyRiskStatusData,
      alignmentRiskStatusData,
      numPolicies,
      completionPercent,
      totalNumControls,
      totalNumTestScore,
      numTechnologyRiskControls,
      technologyRiskData,
      policyAlignmentData,
      policyControlPieData,
      numTotalPolicyControls,
      sliceRows,
      sliceTotalCount,
      sliceHasMore,
      sliceCacheVersion,
      sliceLoading,
      sliceVariables,
      fetchSlice,
      loadMoreSlice,
      selectedPolicyLabel,
      selectedControls,
      projectInfo,
      recommendedPolicy,
      projectStages,
      contextType,
      refetchProjectFromId,
      refetchProjectDimensionsFromId,
      refetch,
      getAttachmentsByControlId,
      refetchAttachments,
    };
  }, [
    project,
    opRiskStatus,
    opRiskColor,
    assessments,
    isOrganization,
    loading,
    phase2Loading,
    dimensionsReady,
    stagesReady,
    projectName,
    projectDimension,
    error,
    technologyRiskStatusData,
    alignmentRiskStatusData,
    numPolicies,
    completionPercent,
    totalNumControls,
    totalNumTestScore,
    numTechnologyRiskControls,
    technologyRiskData,
    policyAlignmentData,
    policyControlPieData,
    numTotalPolicyControls,
    sliceRows,
    sliceTotalCount,
    sliceHasMore,
    sliceCacheVersion,
    sliceLoading,
    sliceVariables,
    fetchSlice,
    loadMoreSlice,
    selectedPolicyLabel,
    selectedControls,
    projectInfo,
    recommendedPolicy,
    projectStages,
    contextType,
    getAttachmentsByControlId,
    refetchAttachments,
  ]);

  return (
    <DashboardDataContext.Provider value={contextValue}>
      {children}
    </DashboardDataContext.Provider>
  );
};
